import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Notebook, LinkSimple, Check, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import api from "../lib/api";
import { RichTextEditor } from "./RichTextEditor";

// A fuller toolbar than the Notes module's text records (per user
// request), but trimmed to just the essentials (2026-09-11): header
// style, background highlight, align, blockquote/code-block/link were
// all removed as unused clutter, keeping size/bold/italic/underline/text
// color/lists/clean. Size uses a custom literal-pixel format ("globalSize",
// registered in RichTextEditor.jsx) instead of Quill's small/large/huge
// keywords, per user request.
const GLOBAL_NOTE_SIZE_OPTIONS = ["8px", "10px", "12px", "14px", "16px", "18px", "24px"];
const GLOBAL_NOTE_TOOLBAR_OPTIONS = [
  [{ globalSize: GLOBAL_NOTE_SIZE_OPTIONS }],
  ["bold", "italic", "underline"],
  [{ color: [] }],
  [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
  ["clean"],
];
const GLOBAL_NOTE_FORMATS = ["globalSize", "bold", "italic", "underline", "color", "list"];

// One free-form rich-text note per account, independent of any note/task/
// schedule item - GET/PATCH /api/global-note. Same fixed-2-second-interval
// autosave architecture as CapturePreviewDialog/TaskDetailDialog (per
// established convention: no debounce, no save-coalescing queue), plus the
// same visibilitychange/pagehide safety net for mobile tab-switching.
// Rendered both as a compact widget on the Tasks sidebar and full-size on
// the dedicated /global-note page - `className` controls sizing per caller.
export function GlobalNoteBox({ className }) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const editorRef = useRef(null);
  const initialHtmlRef = useRef("");
  const latestHtmlRef = useRef("");
  const savedRef = useRef("");
  const savingPromiseRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/global-note");
        initialHtmlRef.current = data.content_html || "";
        latestHtmlRef.current = data.content_html || "";
        savedRef.current = data.content_html || "";
      } catch (e) {
        // Best-effort load - editor still usable, just starts blank.
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const performSave = async () => {
    if (savingPromiseRef.current) await savingPromiseRef.current;
    const current = latestHtmlRef.current;
    if (current === savedRef.current) return;
    setSaveStatus("saving");
    const promise = (async () => {
      try {
        await api.patch("/global-note", { content_html: current });
        savedRef.current = current;
        setSaveStatus("saved");
      } catch (e) {
        setSaveStatus("error");
      }
    })();
    savingPromiseRef.current = promise;
    await promise;
    if (savingPromiseRef.current === promise) savingPromiseRef.current = null;
  };
  // Always points at the latest performSave so the mount-only effects below
  // (interval/visibilitychange/unmount) never close over a stale version.
  const performSaveRef = useRef(() => {});
  performSaveRef.current = performSave;

  useEffect(() => {
    if (!loaded) return;
    const interval = setInterval(() => performSaveRef.current(), 2000);
    return () => clearInterval(interval);
  }, [loaded]);

  useEffect(() => {
    const saveIfHidden = () => {
      if (document.visibilityState === "hidden") performSaveRef.current();
    };
    document.addEventListener("visibilitychange", saveIfHidden);
    window.addEventListener("pagehide", saveIfHidden);
    return () => {
      document.removeEventListener("visibilitychange", saveIfHidden);
      window.removeEventListener("pagehide", saveIfHidden);
    };
  }, []);

  // Flush on unmount too (e.g. navigating away right after typing, before
  // the next 2s tick) - this widget has no "close dialog" moment to hook
  // into like CapturePreviewDialog/TaskDetailDialog do.
  useEffect(() => {
    return () => {
      performSaveRef.current();
    };
  }, []);

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/global-note`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t("globalNote.linkCopied"));
    } catch (e) {
      toast.error(t("common.genericError"));
    }
  };

  return (
    <div className={`border border-border bg-card p-2 flex flex-col gap-1.5 ${className || ""}`} data-testid="global-note-widget">
      <div className="flex items-center justify-between gap-2 shrink-0">
        <p className="font-mono-tag text-xs font-bold flex items-center gap-1.5">
          <Notebook size={14} /> {t("globalNote.title")}
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <span data-testid="global-note-save-status" className="flex items-center gap-1 text-[11px]">
            {saveStatus === "saving" && (
              <span className="flex items-center gap-1 text-muted-foreground"><CircleNotch size={12} className="animate-spin" /> {t("globalNote.saving")}</span>
            )}
            {saveStatus === "saved" && (
              <span className="flex items-center gap-1 text-muted-foreground"><Check size={12} weight="bold" /> {t("globalNote.saved")}</span>
            )}
            {saveStatus === "error" && (
              <span className="flex items-center gap-1 text-destructive"><WarningCircle size={12} weight="bold" /> {t("globalNote.saveFailed")}</span>
            )}
          </span>
          <button
            type="button"
            data-testid="global-note-copy-link-button"
            onClick={handleCopyLink}
            title={t("globalNote.copyLinkTooltip")}
            className="p-1 text-muted-foreground hover:text-primary shrink-0"
          >
            <LinkSimple size={15} />
          </button>
        </div>
      </div>
      {loaded && (
        <RichTextEditor
          ref={editorRef}
          defaultValue={initialHtmlRef.current}
          className="global-note-editor-shell rich-editor-shell flex-1 min-h-0"
          dataTestId="global-note-editor"
          toolbarOptions={GLOBAL_NOTE_TOOLBAR_OPTIONS}
          formats={GLOBAL_NOTE_FORMATS}
          onChange={(html) => {
            latestHtmlRef.current = html;
          }}
        />
      )}
    </div>
  );
}
